// PUT /api/projects/:id/dashboard/config — Phase 3b.
//
// SetupBanner's "Apply" + the customise editor's "Save" call this. On
// success the banner animates out + the config hook refetches; on the
// typed `concurrent_write` 409 the SPA keeps the customer's pending
// changes in the form and surfaces a "refresh to see the latest" toast.

import { useCallback, useState } from "react";

import {
  ApplyMappingResponseSchema,
  type ApplyMappingPayload,
  type ApplyMappingResponse,
} from "@loom/shared/dashboard";

import { HttpError, httpPut } from "../lib/http";

export type ApplyMappingError =
  | { readonly kind: "concurrent_write" }
  | { readonly kind: "other"; readonly message: string };

export interface ApplyMappingState {
  readonly apply: (payload: ApplyMappingPayload) => Promise<ApplyMappingResponse>;
  readonly saving: boolean;
  readonly error: ApplyMappingError | null;
}

export const useApplyMapping = (projectId: number): ApplyMappingState => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApplyMappingError | null>(null);

  const apply = useCallback(
    async (payload: ApplyMappingPayload): Promise<ApplyMappingResponse> => {
      setSaving(true);
      setError(null);
      try {
        const res = await httpPut(
          `/api/projects/${projectId}/dashboard/config`,
          payload,
          ApplyMappingResponseSchema,
        );
        return res;
      } catch (err) {
        if (err instanceof HttpError && err.status === 409) {
          const e: ApplyMappingError = { kind: "concurrent_write" };
          setError(e);
          throw err;
        }
        const e: ApplyMappingError = {
          kind: "other",
          message: err instanceof Error ? err.message : String(err),
        };
        setError(e);
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [projectId],
  );

  return { apply, saving, error };
};
